import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Menu, X, User, LogOut } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { useAuthService } from '../../services/authService'
import Logo from '../ui/Logo'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { isLoggedIn, userDetails } = useAuthStore()
  const { handleLogout } = useAuthService()

  const isAdmin =
    (userDetails?.user_level || 0) >= 99 ||
    userDetails?.permissions?.includes("*") ||
    userDetails?.permissions?.includes("event.manage");

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const handleLogoutClick = () => {
    handleLogout()
    setIsMenuOpen(false)
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <Logo />
            <span className="text-xl font-bold text-gray-900">PhilTrade</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className="text-gray-700 hover:text-primary-600 transition-colors duration-200"
              activeProps={{ className: "text-primary-600 font-medium" }}
            >
              Home
            </Link>
            <Link
              to="/events"
              className="text-gray-700 hover:text-primary-600 transition-colors duration-200"
              activeProps={{ className: "text-primary-600 font-medium" }}
            >
              Events
            </Link>
            <Link
              to="/contact"
              className="text-gray-700 hover:text-primary-600 transition-colors duration-200"
              activeProps={{ className: "text-primary-600 font-medium" }}
            >
              Contact Us
            </Link>
          </nav>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {isLoggedIn ? (
              <div className="flex items-center space-x-4">
                <Link
                  to="/profile"
                  className="text-gray-700 hover:text-primary-600 transition-colors duration-200 font-medium"
                >
                  Welcome, {userDetails?.firstName}
                </Link>
                {isAdmin && (
                  <>
                    <Link
                      to="/dashboard"
                      className="text-gray-700 hover:text-primary-600 transition-colors duration-200"
                    >
                      Admin
                    </Link>
                  </>
                )}
                <button
                  onClick={handleLogoutClick}
                  className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors duration-200"
                >
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-primary-600 transition-colors duration-200"
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors duration-200"
                >
                  Sign up
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <nav className="flex flex-col space-y-4">
              <Link
                to="/"
                className="text-gray-700 hover:text-primary-600 transition-colors duration-200 px-4 py-2"
                activeProps={{ className: "text-primary-600 font-medium" }}
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/events"
                className="text-gray-700 hover:text-primary-600 transition-colors duration-200 px-4 py-2"
                activeProps={{ className: "text-primary-600 font-medium" }}
                onClick={() => setIsMenuOpen(false)}
              >
                Events
              </Link>
              <Link
                to="/contact"
                className="text-gray-700 hover:text-primary-600 transition-colors duration-200 px-4 py-2"
                activeProps={{ className: "text-primary-600 font-medium" }}
                onClick={() => setIsMenuOpen(false)}
              >
                Contact Us
              </Link>
              {isLoggedIn && isAdmin && (
                <>
                  <Link
                    to="/dashboard"
                    className="text-gray-700 hover:text-primary-600 transition-colors duration-200 px-4 py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/dashboard"
                    className="text-gray-700 hover:text-primary-600 transition-colors duration-200 px-4 py-2"
                    activeProps={{ className: "text-primary-600 font-medium" }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Admin
                  </Link>
                </>
              )}
            </nav>

            {/* Mobile Auth Buttons */}
            <div className="border-t border-gray-200 mt-4 pt-4 px-4">
              {isLoggedIn ? (
                <div className="flex flex-col">
                  <Link
                    to="/profile"
                    className="flex items-center space-x-3 text-gray-700 hover:text-primary-600 transition-colors duration-200 group"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-primary-50 transition-colors duration-200">
                      <User size={20} className="text-gray-500 group-hover:text-primary-600" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-semibold text-gray-900">{userDetails?.firstName} {userDetails?.lastName}</span>
                      <span className="text-xs text-gray-500 truncate max-w-[180px]">{userDetails?.email}</span>
                    </div>
                  </Link>

                  <button
                    onClick={handleLogoutClick}
                    className="flex items-center space-x-2 text-red-600 hover:text-red-700 transition-colors duration-200 py-2 mt-2"
                  >
                    <LogOut size={18} />
                    <span className="font-medium">Logout</span>
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Link
                    to="/login"
                    className="bg-secondary-600 text-white px-4 py-2.5 rounded-lg hover:bg-secondary-700 transition-all duration-200 text-center font-semibold shadow-sm active:scale-[0.98]"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/signup"
                    className="bg-primary-600 text-white px-4 py-2.5 rounded-lg hover:bg-primary-700 transition-all duration-200 text-center font-semibold shadow-sm active:scale-[0.98]"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign up
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header
